import React, { useEffect, useRef, useState } from 'react';
import { ActionIcon, Checkbox, Combobox, Group, InputBase, useCombobox } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus as faPlusDef, faXmark as faXmarkDef, faChevronDown as faChevronDownDef } from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import type { CategoryReportGroup } from '../../../../../services/categories/categories.service';
import { UNCATEGORIZED_LABEL } from '../../orders/menu/category-menu.utils';
import classes from '../shared/product-settings.module.scss';

const faPlus = faPlusDef as IconProp;
const faXmark = faXmarkDef as IconProp;
const faChevronDown = faChevronDownDef as IconProp;

type CategoryOption = { value: string; label: string };

type InlineCategoryCreateFieldProps = {
  options: CategoryOption[];
  value: number | null;
  onChange: (categoryId: number | null) => void;
  onCreateCategory: (name: string, reportGroup: CategoryReportGroup | null) => Promise<number | null>;
  label?: React.ReactNode;
  disabled?: boolean;
};

// Architectural summary: Searchable category field that inline-creates when nothing matches.
const InlineCategoryCreateFieldComponent: React.FC<InlineCategoryCreateFieldProps> = ({
  options,
  value,
  onChange,
  onCreateCategory,
  label = 'קטגוריה',
  disabled = false,
}) => {
  const combobox = useCombobox({
    onDropdownOpen: () => setReportGroup(null),
    onDropdownClose: () => combobox.resetSelectedOption(),
  });
  const [search, setSearch] = useState('');
  const [reportGroup, setReportGroup] = useState<CategoryReportGroup | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const createInFlightRef = useRef(false);

  const selectedLabel = options.find((option) => option.value === (value === null ? '' : String(value)))?.label ?? '';
  const trimmed = search.trim();
  const filtered = options.filter((option) => option.label.toLowerCase().includes(trimmed.toLowerCase()));
  const canCreate = trimmed.length > 0 && filtered.length === 0;

  // Reflect the selected label while closed; skip SSE-pending selections to avoid a blank flash.
  useEffect(() => {
    if (combobox.dropdownOpened) return;
    if (value === null) {
      setSearch('');
    } else if (selectedLabel) {
      setSearch(selectedLabel);
    }
  }, [value, selectedLabel, combobox]);

  const toggleReportGroup = (group: CategoryReportGroup) =>
    setReportGroup((previous) => (previous === group ? null : group));

  const handleCreate = async () => {
    if (!canCreate || createInFlightRef.current) {
      return;
    }
    createInFlightRef.current = true;
    setIsCreating(true);
    try {
      const newId = await onCreateCategory(trimmed, reportGroup);
      if (newId !== null) {
        notifications.show({ color: 'green', title: 'קטגוריה נוספה', message: trimmed, autoClose: 3000 });
        onChange(newId);
        setSearch(trimmed);
        combobox.closeDropdown();
      }
    } finally {
      createInFlightRef.current = false;
      setIsCreating(false);
    }
  };

  const rightSection = canCreate ? (
    <ActionIcon
      aria-label="צור קטגוריה"
      data-testid="inline-category-create"
      variant="subtle"
      color="blue"
      size="sm"
      loading={isCreating}
      // iOS drops the synthesized click after a preventDefaulted touch; fire on pointerdown itself.
      onPointerDown={(event) => {
        event.preventDefault();
        event.stopPropagation();
        void handleCreate();
      }}
      // Desktop focus retention; on iOS the preventDefaulted pointerdown already suppresses this.
      onMouseDown={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
      // Mouse/keyboard fallback; createInFlightRef (sync) no-ops the duplicate when both land.
      onClick={(event) => {
        event.stopPropagation();
        void handleCreate();
      }}
    >
      <FontAwesomeIcon icon={faPlus} />
    </ActionIcon>
  ) : value !== null ? (
    <ActionIcon
      aria-label="נקה קטגוריה"
      data-testid="inline-category-clear"
      variant="subtle"
      color="gray"
      size="sm"
      onMouseDown={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
      onClick={(event) => {
        event.stopPropagation();
        onChange(null);
        setSearch('');
      }}
    >
      <FontAwesomeIcon icon={faXmark} />
    </ActionIcon>
  ) : (
    <FontAwesomeIcon icon={faChevronDown} size="xs" />
  );

  return (
    <Combobox
      store={combobox}
      disabled={disabled}
      position="bottom"
      middlewares={{ flip: false, shift: true }}
      onOptionSubmit={(optionValue) => {
        onChange(Number(optionValue));
        setSearch(options.find((option) => option.value === optionValue)?.label ?? '');
        combobox.closeDropdown();
      }}
    >
      <Combobox.Target>
        <InputBase
          data-testid="inline-category-input"
          label={label}
          radius="md"
          disabled={disabled}
          value={search}
          placeholder={UNCATEGORIZED_LABEL}
          rightSection={rightSection}
          rightSectionPointerEvents={canCreate || value !== null ? 'all' : 'none'}
          onChange={(event) => {
            combobox.openDropdown();
            combobox.updateSelectedOptionIndex();
            setSearch(event.currentTarget.value);
          }}
          onClick={() => combobox.openDropdown()}
          onFocus={() => combobox.openDropdown()}
          // Keep a pending unmatched name alive on blur so the touch create can complete.
          // Also skip while a create is in flight: clicking the "+" action blurs the
          // input before handleCreate's async work resolves, and this stale-closure
          // reset would otherwise clear the just-created category back to empty.
          onBlur={() => {
            if (canCreate || createInFlightRef.current) return;
            combobox.closeDropdown();
            setSearch(value === null ? '' : selectedLabel);
          }}
        />
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Options>
          {filtered.length > 0 ? (
            filtered.map((option) => (
              <Combobox.Option value={option.value} key={option.value} data-testid={`inline-category-option-${option.value}`}>
                {option.label}
              </Combobox.Option>
            ))
          ) : canCreate ? (
            <div
              className={classes.createAffordance}
              // Desktop focus retention only; touch relies on the onBlur canCreate guard (no pointerdown guard).
              onMouseDown={(event) => event.preventDefault()}
            >
              <span className={classes.createHint}>צור קטגוריה: {trimmed}</span>
              <Group gap="md">
                <Checkbox
                  data-testid="inline-category-group-food"
                  label="אוכל"
                  checked={reportGroup === 'food'}
                  disabled={isCreating}
                  onChange={() => toggleReportGroup('food')}
                />
                <Checkbox
                  data-testid="inline-category-group-beverages"
                  label="משקאות"
                  checked={reportGroup === 'beverages'}
                  disabled={isCreating}
                  onChange={() => toggleReportGroup('beverages')}
                />
              </Group>
            </div>
          ) : (
            <Combobox.Empty>לא נמצאו קטגוריות</Combobox.Empty>
          )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
};

export const InlineCategoryCreateField = React.memo(InlineCategoryCreateFieldComponent);
